import { InfoCircleTwoTone } from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Typography,
  Modal,
  Space,
  Card,
} from 'antd';
import Clipboard, { Event } from 'clipboard';
import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import IconCopy from '@/assets/images/public/icon-copy.svg';
import { MultiApp } from '@/config/context';
import { getUserId, isInternal } from '@/infrastructure/auth';
import { BASE_NAME } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { getPid, getProductId } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { useReportPage } from '@/infrastructure/reporter/datong/report';
import { AppSlot } from '@/presentation/components/app-slot';
import { hintText } from '@/presentation/components/hint-text/hintText';
import useToggle from '@/presentation/components/toggle/use-toggle';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import { isGameType } from '@/presentation/routes/entries/game-performance';
import { OrgType } from '@/proto/manage';
import { IUpdateProductOaReq, GAME_ENGINE_OPTIONS, productOAServer } from '@/service/productoa';
import ProductInfoPro from '../info-pro';
import {
  getEpcBizList,
  getEpcSubBizList,
  deleteApp,
  updateAppInfo,
} from './api';
import style from './style.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 20 },
};

export interface DeleteAppReq {
  appId: string;
  pid: string;
  userId: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

enum AppFormMap {
  Unknown = 0,
  APP = 1,
  SDK = 2,
  Extension = 3,
  CrossPlatform = 4
}

export enum OrgTypeName {
  Unknown = OrgType.UNKNOWN_ORG,
  Company = OrgType.COMPANY,
  Organization = OrgType.ORGANIZATION,
  Individual = OrgType.INDIVIDUAL
}

enum AppPlatformMap {
  Unknown = -1,
  Android = 1,
  iOS = 2,
  Mac = 4,
  Flutter = 9,
  Windows = 10,
  Linux = 5,
  Harmony = 11,
  VisionOS = 17,
  AppleTV = 20,
}

enum AppTypeMap {
  Unknown = 0,
  OfficeBusiness = 3,
  ChatCommunication = 4,
  SystemInput = 5,
  MobileBeautification = 6,
  ReadingLearning = 7,
  TravelMap = 8,
  AudioVideo = 9,
  ChildrenParenting = 10,
  PhotoBeautification = 11,
  ShoppingFashion = 12,
  LifeEntertainment = 13,
  UtilityTools = 14,
  ActionAdventure = 15,
  FlightShooting = 16,
  RolePlaying = 17,
  BusinessStrategy = 18,
  SportsRacing = 19,
  OnlineGame = 20,
  PuzzleGame = 21,
  CasualEntertainment = 22,
};

const getAppFormName = (appForm: number | undefined, t: Translate) => {
  switch (appForm ?? AppFormMap.Unknown) {
    case AppFormMap.APP:
      return 'APP';
    case AppFormMap.SDK:
      return 'SDK';
    case AppFormMap.Extension:
      return 'Extension';
    case AppFormMap.CrossPlatform:
      return t('跨端产品');
    default:
      return '-';
  }
};

export const getOrgTypeName = (orgType: number | undefined, t: Translate) => {
  switch (orgType ?? OrgTypeName.Unknown) {
    case OrgTypeName.Company:
      return t('公司');
    case OrgTypeName.Organization:
      return t('组织');
    case OrgTypeName.Individual:
      return t('个人');
    default:
      return t('未知');
  }
};

const getAppPlatformName = (platformId: number | undefined, t: Translate) => {
  switch (platformId ?? AppPlatformMap.Unknown) {
    case AppPlatformMap.Android:
      return 'Android';
    case AppPlatformMap.iOS:
      return 'iOS';
    case AppPlatformMap.Mac:
      return 'Mac';
    case AppPlatformMap.Flutter:
      return 'Flutter';
    case AppPlatformMap.Windows:
      return 'Windows';
    case AppPlatformMap.Linux:
      return 'Linux';
    case AppPlatformMap.Harmony:
      return 'Harmony';
    case AppPlatformMap.VisionOS:
      return 'VisionOS';
    case AppPlatformMap.AppleTV:
      return 'AppleTV';
    case AppPlatformMap.Unknown:
      return t('未知');
    default:
      return t('其他');
  }
};

const getAppTypeName = (appType: number | undefined, t: Translate) => {
  switch (appType ?? AppTypeMap.Unknown) {
    case AppTypeMap.OfficeBusiness:
      return t('办公商务');
    case AppTypeMap.ChatCommunication:
      return t('聊天通讯');
    case AppTypeMap.SystemInput:
      return t('系统输入');
    case AppTypeMap.MobileBeautification:
      return t('手机美化');
    case AppTypeMap.ReadingLearning:
      return t('阅读学习');
    case AppTypeMap.TravelMap:
      return t('出行地图');
    case AppTypeMap.AudioVideo:
      return t('影音视听');
    case AppTypeMap.ChildrenParenting:
      return t('儿童亲子');
    case AppTypeMap.PhotoBeautification:
      return t('拍摄美化');
    case AppTypeMap.ShoppingFashion:
      return t('购物时尚');
    case AppTypeMap.LifeEntertainment:
      return t('生活娱乐');
    case AppTypeMap.UtilityTools:
      return t('实用工具');
    case AppTypeMap.ActionAdventure:
      return t('动作冒险');
    case AppTypeMap.FlightShooting:
      return t('飞行射击');
    case AppTypeMap.RolePlaying:
      return t('角色扮演');
    case AppTypeMap.BusinessStrategy:
      return t('经营策略');
    case AppTypeMap.SportsRacing:
      return t('体育竞速');
    case AppTypeMap.OnlineGame:
      return t('网络游戏');
    case AppTypeMap.PuzzleGame:
      return t('益智游戏');
    case AppTypeMap.CasualEntertainment:
      return t('休闲娱乐');
    default:
      return '-';
  }
};

export interface UpdateAppInfoReq {
  appId: string;
  desc: string;
  epcBizId: string;
  epcBizName: string;
  epcSubBizId: string;
  epcSubBizName: string;
  logoUrl: string;
  name: string;
  pid: number;
  pkgSign: string;
  type: number;
  userId: string;
}


interface AppInfoType {
  productName: string;
  department: string;
  productId: string;
  productKey: string;
  appForm: number;
  platformId: number;
  type: number;
  creator: string;
  createTime: string;
  description: string;
  pkgSign: string;
  bundleId: string;
  epc: {
    bizId: string;
    bizName: string;
    subBizId: string;
    subBizName: string;
  };
  diagnoseAppID: string;
  diagnoseAppKey: string;
  token: string;
  gameEngine: number;
}

interface OptionsType {
  label: string;
  value: string;
}

interface FieldConfig {
  key: string;
  label: React.ReactNode;
  content: React.ReactNode;
  visible?: boolean;
}

const CopyableField = ({ id, value, copyTitle }: { id: string; value?: string; copyTitle: string }) => (
  <>
    <span id={id}>{value}</span>
    <img
      id="copyBtn"
      style={{ paddingLeft: '8px', cursor: 'pointer' }}
      title={copyTitle}
      src={IconCopy}
      alt="Copy to clipboard"
      data-clipboard-target={`#${id}`}
    />
  </>
);

const ProductInfo = () => {
  const { t } = useTranslation();
  useReportPage(PAGE_ID.setting.productInfo);
  const [productEnable] = useToggle('product', ['user']);
  const [clipboard, setClipboard] = useState<any>(null);
  const [appInfo, setAppInfo] = useState<AppInfoType>();
  const [epcBizListData, setEpcBizListData] = useState<OptionsType[]>();
  const [epcSubBizListData, setEpcSubBizListData] = useState<OptionsType[]>();
  const [isAdmin, setIsAdmin] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    const data = await productOAServer.getProductOaInfo({ appId: getProductId() });
    if (Array.isArray(data) || !data) return;
    const info = {
      productName: data?.name,
      department: data?.department,
      productId: data?.appId,
      productKey: data?.appKey,
      appForm: data?.appForm,
      platformId: data?.platformId,
      type: data?.type,
      creator: data?.ownerId,
      createTime: data?.createTime,
      description: data?.description,
      pkgSign: data?.pkgSign,
      epc: data?.epc,
      bundleId: data?.bundleId,
      diagnoseAppID: data?.diagnoseAppId,
      diagnoseAppKey: data?.diagnoseAppKey,
      token: data?.token,
      gameEngine: data.gameEngine,
    };
    setAppInfo(info);

    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
    const epcBizList = await getEpcBizList();
    const epcBizListData = [
      {
        label: t('不关联'),
        value: '--',
      },
    ];
    if (epcBizList) {
      epcBizListData.push(...epcBizList.map((item: { bizKey: string, bizName: string }) => ({
        label: item.bizName,
        value: item.bizKey,
      })));
    }
    setEpcBizListData(epcBizListData);
  };

  useEffect(() => {
    load();
    setClipboard(new Clipboard('#copyBtn'));
    return () => clipboard?.destroy();
  }, []);

  useEffect(() => {
    clipboard?.on('success', () => {
      message.destroy();
      message.success(t('复制成功'));
    });
    clipboard?.on('error', (e: Event) => {
      message.destroy();
      message.error(t('复制失败'));
      logger.error('clipboard', e?.trigger, e?.action);
    });
  }, [clipboard]);

  useEffect(() => {
    if (appInfo?.epc?.bizId && !isInternal()) {
      loadEpcSubBiz(appInfo.epc.bizId);
    }
  }, [appInfo]);

  const submitAppInfo = async (params: IUpdateProductOaReq) => {
    const data = await productOAServer.updateProductOa(params);
    if (data) {
      message.success(t('保存成功'));
    }
  };
  const oldSubmitAppInfo = async (params: UpdateAppInfoReq) => {
    const data = await updateAppInfo(params);
    if (data.code === 200) {
      message.success(t('保存成功'));
    }
  };
  const onFinish = (values: any) => {
    if (!appInfo) return;

    // TODO 接口切换暂时添加白名单
    if (productEnable) {
      let params: IUpdateProductOaReq = {
        appId: appInfo.productId,
        desc: values.description,
        name: appInfo.productName,
        platformId: appInfo.platformId,
        sign: appInfo.pkgSign,
        type: appInfo.type,
        userId: getUserId(),
        bizId: appInfo?.epc?.bizId || '',
        bizName: appInfo?.epc?.bizName || '',
        subBizId: appInfo?.epc?.subBizId || '',
        subBizName: appInfo?.epc?.subBizName || '',
      };
      if (values?.epc?.bizId && values?.epc?.bizId !== '--') {
        if (!values?.epc?.subBizId) {
          return;
        }
        const bizName = epcBizListData?.filter(item => item.value === values?.epc?.bizId)?.[0].label;
        const subBizName = epcSubBizListData?.filter(item => item.value === values.epc.subBizId)?.[0].label;
        params = {
          ...params,
          bizId: values.epc.bizId,
          bizName: bizName || '',
          subBizId: values.epc.subBizId,
          subBizName: subBizName || '',
        };
      }
      submitAppInfo(params);
    } else {
      let params = {
        appId: appInfo.productId,
        desc: values.description,
        logoUrl: '',
        name: appInfo.productName,
        pid: appInfo.platformId,
        pkgSign: values.pkgSign,
        type: appInfo.type,
        userId: getUserId(),
        epcBizId: '',
        epcBizName: '',
        epcSubBizId: '',
        epcSubBizName: '',
      };
      if (values?.epc?.bizId && values?.epc?.bizId !== '--') {
        if (!values.epc.subBizId) {
          return;
        }
        const bizName = epcBizListData?.filter(item => item.value === values.epc.bizId)?.[0].label;
        const subBizName = epcSubBizListData?.filter(item => item.value === values.epc.subBizId)?.[0].label;
        params = {
          ...params,
          epcBizId: values.epc.bizId,
          epcBizName: bizName || '',
          epcSubBizId: values.epc.subBizId,
          epcSubBizName: subBizName || '',
        };
      }
      oldSubmitAppInfo(params);
    }
  };
  const loadEpcSubBiz = async (value: string) => {
    const epcSubBizList = await getEpcSubBizList({ id: value });
    let epcSubBizListData = [];
    if (epcSubBizList.subBizList) {
      epcSubBizListData = epcSubBizList.subBizList
        .map((item: { productId: string, productName: string }) => ({
          label: item.productName,
          value: item.productId,
        }));
    }
    setEpcSubBizListData(epcSubBizListData);
  };

  const onDelete = async () => {
    Modal.confirm({
      title: t('删除产品{{productName}}', { productName: appInfo?.productName }),
      icon: <InfoCircleTwoTone />,
      content: t('已清楚了解删除产品产生的影响，并确定要删除产品？'),
      onOk: async () => {
        const pid = getPid();
        if (productEnable) {
          const rsp = await productOAServer.deleteProductOa({
            appId: getProductId(),
            platformId: pid ? Number(pid) : undefined,
            userId: getUserId(),
          });
          if (!rsp) {
            message.success(t('删除失败'));
            return;
          }
        } else {
          await deleteApp({
            appId: getProductId(),
            pid,
            userId: getUserId(),
          });
        }

        message.success(t('删除产品成功'));
        location.href = `${BASE_NAME}/workbench/apps`;
      },
    });
  };

  const renderPlatform = () => getAppPlatformName(appInfo?.platformId, t);

  const fieldConfigs: FieldConfig[] = [
    { key: 'productName', label: t('产品名称'), content: <span>{appInfo?.productName}</span> },
    {
      key: 'department',
      label: t('所属BG/部门'),
      content: <span className={style.department}>{appInfo?.department && appInfo.department !== '0' ? appInfo.department : '-'}</span>,
    },
    { key: 'appId', label: 'APP ID', content: <CopyableField id="appId" value={appInfo?.productId} copyTitle={t('复制APPID')} /> },
    { key: 'appKey', label: 'APP KEY', content: <CopyableField id="appKey" value={appInfo?.productKey} copyTitle={t('复制APPKEY')} /> },
    {
      key: 'diagnoseAppID',
      label: hintText(t('日志 APP ID'), t('2024年4月1日前，在日志独立Portal创建的产品有独立的APP ID和APP KEY。之后创建的产品，采用统一的APP ID和APP KEY。')),
      content: <CopyableField id="diagnoseAppId" value={appInfo?.diagnoseAppID || '-'} copyTitle={t('复制APPID')} />,
      visible: !!appInfo?.diagnoseAppID,
    },
    {
      key: 'diagnoseAppKey',
      label: t('日志 APP KEY'),
      content: <CopyableField id="diagnoseAppKey" value={appInfo?.diagnoseAppKey || '-'} copyTitle={t('复制APPKEY')} />,
      visible: !!appInfo?.diagnoseAppKey,
    },
    { key: 'bundleId', label: 'Bundle ID', content: <CopyableField id="bundleId" value={appInfo?.bundleId} copyTitle={t('复制BundleID')} /> },
    { key: 'appForm', label: t('产品形态'), content: <span>{getAppFormName(appInfo?.appForm, t)}</span> },
    { key: 'platform', label: t('平台'), content: <span>{renderPlatform()}</span> },
    { key: 'type', label: t('产品类型'), content: <span>{getAppTypeName(appInfo?.type, t)}</span> },
    {
      key: 'gameEngine',
      label: t('游戏引擎'),
      content: <span>{t(GAME_ENGINE_OPTIONS.find(o => o.value === appInfo?.gameEngine)?.label ?? '-')}</span>,
      visible: isGameType(appInfo?.type),
    },
    { key: 'creator', label: t('创建人'), content: <span>{appInfo?.creator}</span> },
    { key: 'createTime', label: t('创建时间'), content: <span>{appInfo?.createTime}</span> },
    {
      key: 'token',
      label: hintText('Token', t('用于调用 OpenAPI 的鉴权 Token，请妥善保管。')),
      content: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Text id="token" ellipsis={true} title={appInfo?.token} style={{ maxWidth: 400 }}>{appInfo?.token}</Text>
          <img
            id="copyBtn"
            style={{ paddingLeft: '8px', cursor: 'pointer', flexShrink: 0 }}
            title={t('复制 OpenAPI Token')}
            src={IconCopy}
            alt="Copy to clipboard"
            data-clipboard-target="#token"
          />
        </div>
      ),
      visible: !!appInfo?.token,
    },
  ];

  const visibleFields = fieldConfigs.filter(f => f.visible !== false);
  const fieldRows: FieldConfig[][] = [];
  for (let i = 0; i < visibleFields.length; i += 2) {
    fieldRows.push(visibleFields.slice(i, i + 2));
  }

  return <Card bordered={false}>
    <div className={style.productInfoContainer}>
      <Form
        {...layout}
        style={{ width: '100%' }}
        form={form}
        onFinish={onFinish}
      >
        {fieldRows.map((row, rowIdx) => (
          <Row key={rowIdx} gutter={12}>
            {row.map(field => (
              <Col key={field.key} span={12}>
                <Form.Item label={field.label}>{field.content}</Form.Item>
              </Col>
            ))}
          </Row>
        ))}
        <Form.Item
          labelCol={{ span: 2 }}
          wrapperCol={{ span: 22 }}
          name="description"
          label={t('产品描述')}
          className={style.formItem}
          key={appInfo?.description}
          initialValue={appInfo?.description}
        >
          <TextArea
            maxLength={500}
            showCount={true}
            placeholder={t('请输入内容')}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldsValue({
              description: event.target.value,
            })}
          />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 2, span: 22 }}>
          <Button disabled={!isAdmin} style={{ marginRight: '0' }} type="primary" htmlType="submit">
            {t('保存')}
          </Button>
        </Form.Item>
        <div style={{ padding: '30px 0', textAlign: 'right' }}>
          <Space size={22}>
            <Text type="warning">
              {t('将该产品从所有成员的产品列表中移除，并永久删除该产品的所有数据。此操作不可撤销，请谨慎操作。')}
            </Text>
            <Button style={{ background: '#EDF1F7', border: 'none' }} onClick={onDelete} disabled={!isAdmin}>{t('删除产品')}</Button>
          </Space>
        </div>
      </Form>
    </div>
  </Card>;
};

export default () => (
  <Fragment>
    <AppSlot apps={[MultiApp.WOA, MultiApp.Internal]}>
      <ProductInfo />
    </AppSlot>
    <AppSlot apps={[MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu]}>
      <ProductInfoPro />
    </AppSlot>
  </Fragment>
);
